import { c as _c } from "react/compiler-runtime";
import './child-spotlight-card.scss';
import { Fragment, type ReactNode } from 'react';
import { ChildImage } from '@sola/ark-core/atoms/child-image/child-image.tsx';
import { ChildImageVariantEnum } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';
export interface ChildSpotlightCardProps {
  /** Image element (e.g. `<ChildImage … />`) or a fallback. */
  image?: ReactNode;
  /** Overlay shown in the image pane (e.g. `<Badge … />`). */
  badge?: ReactNode;
  hasRibbon?: boolean;
  children: ReactNode;
}
const DefaultRibbon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="ChildSpotlightCard__ribbon" aria-hidden={true}><span /><span /><span /><span /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const ChildSpotlightCard: React.FC<ChildSpotlightCardProps> = t0 => {
  const $ = _c(15);
  const {
    image,
    badge,
    hasRibbon: t1,
    children
  } = t0;
  const hasRibbon = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== hasRibbon) {
    t2 = hasRibbon && <DefaultRibbon />;
    $[0] = hasRibbon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== badge) {
    t3 = badge && <div className="ChildSpotlightCard__badge absolute top-comfy-xs left-comfy-xs">{badge}</div>;
    $[2] = badge;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== image || $[5] !== t3) {
    t4 = <div className="ChildSpotlightCard__imagePane relative overflow-hidden w-full aspect-ratio-16-9 md:h-full md:aspect-ratio-1">{image}{t3}</div>;
    $[4] = image;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children) {
    t5 = <div className="ChildSpotlightCard__content min-w-0 w-full p-comfy-lg">{children}</div>;
    $[7] = children;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="ChildSpotlightCard__body flex flex-1 min-h-0 flex-col md:grid md:grid-cols-2">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t6) {
    t7 = <div className="ChildSpotlightCard @container flex flex-col overflow-hidden w-full rounded-lg">{t2}{t6}</div>;
    $[12] = t2;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
export interface ChildSpotlightImageProps {
  childImageUrl?: string | null;
  alt: string;
  variantMd?: ChildImageVariantEnum;
}
export const ChildSpotlightImage: React.FC<ChildSpotlightImageProps> = t0 => {
  const $ = _c(15);
  const {
    childImageUrl,
    alt,
    variantMd
  } = t0;
  if (!childImageUrl) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="ChildSpotlightCard__image--fallback w-full h-full" aria-hidden={true} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== alt || $[2] !== childImageUrl || $[3] !== variantMd) {
    t1 = variantMd && <div aria-hidden="true"><ChildImage className="block w-full h-full object-fit-cover object-position-top md:hidden" childImageUrl={childImageUrl} alt={alt} variant={ChildImageVariantEnum.Landscape} width={900} /></div>;
    $[1] = alt;
    $[2] = childImageUrl;
    $[3] = variantMd;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!variantMd;
  let t3;
  if ($[5] !== t2) {
    t3 = cn("ChildSpotlightCard__image object-fit-cover block", {
      "ChildSpotlightCard__image--scale sr-only md:not-sr-only": t2
    });
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = variantMd ?? ChildImageVariantEnum.Landscape;
  let t5;
  if ($[7] !== alt || $[8] !== childImageUrl || $[9] !== t3 || $[10] !== t4) {
    t5 = <ChildImage className={t3} childImageUrl={childImageUrl} alt={alt} variant={t4} width={900} />;
    $[7] = alt;
    $[8] = childImageUrl;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t5) {
    t6 = <>{t1}{t5}</>;
    $[12] = t1;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
export interface ChildStatsProps {
  items: {
    icon: ReactNode;
    label: ReactNode;
  }[];
}
export const ChildStats: React.FC<ChildStatsProps> = t0 => {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="ChildSpotlightCard__stats flex w-full items-stretch justify-center gap-snug-xl">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export interface ChildSpotlightCardActionItemProps {
  children: ReactNode;
  className?: string;
}
export const ChildSpotlightCardActionItem: React.FC<ChildSpotlightCardActionItemProps> = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("ChildSpotlightCard__actionItem", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
function _temp(t0, index) {
  const {
    icon,
    label
  } = t0;
  return <Fragment key={index}>{index > 0 && <span className="ChildSpotlightCard__statsDivider self-stretch" aria-hidden={true} />}<span className="ChildSpotlightCard__stat flex min-w-0 flex-col items-center gap-snug-md text-center label-sm label-neutral-mild">{icon}<span>{label}</span></span></Fragment>;
}